const sendDataLayerPageView = async () => {
    await checkIfDataLayerReady();
    await checkIfUserAuthReady();

    const payload = {
        event: 'customer_identified',
        ...getDataLayerUserPayload(),
    };

    window.dataLayer!.push(payload);
};

const sendDataLayerEvent = async (event: string) => {
    await checkIfDataLayerReady();
    await checkIfUserAuthReady();

    const payload = {
        event: event,
        ...getDataLayerUserPayload(),
    };

    window.dataLayer!.push(payload);
};

const sendDataLayerSearchEvent = async (event: string, query: string) => {
    await checkIfDataLayerReady();
    await checkIfUserAuthReady();

    const payload = {
        event: event,
        search_term: query,
        ...getDataLayerUserPayload(),
    };

    window.dataLayer!.push(payload);
};

const sendDataLayerEcommerceEvent = async (
    event: string,
    funnelAttribution: any,
    ecommercePayload: any,
) => {
    await checkIfDataLayerReady();
    await checkIfUserAuthReady();

    ecommercePayload.item_list_name = normalizeEcommerceItemAttribute(
        ecommercePayload.item_list_name,
    );

    for (let i = 0; i < ecommercePayload?.items?.length; i++) {
        ecommercePayload.items[i].item_name = ecommercePayload.items[
            i
        ].item_name
            ?.replace(/(?:^|[\s"'([{])+\S/g, (match: any) =>
                match.toUpperCase(),
            )
            .replace(/ X /g, ' x ');
        ecommercePayload.items[i].item_category =
            normalizeEcommerceItemAttribute(
                ecommercePayload.items[i].item_category,
            );
        ecommercePayload.items[i].item_category2 =
            normalizeEcommerceItemAttribute(
                ecommercePayload.items[i].item_category2,
            );
        ecommercePayload.items[i].item_category3 =
            normalizeEcommerceItemAttribute(
                ecommercePayload.items[i].item_category3,
            );
        ecommercePayload.items[i].item_category5 =
            normalizeEcommerceItemAttribute(
                ecommercePayload.items[i].item_category5,
            );
        ecommercePayload.items[i].color = normalizeEcommerceItemAttribute(
            ecommercePayload.items[i].color,
        );
        ecommercePayload.items[i].finish = normalizeEcommerceItemAttribute(
            ecommercePayload.items[i].finish,
        );
        ecommercePayload.items[i].material = normalizeEcommerceItemAttribute(
            ecommercePayload.items[i].material,
        );
    }

    const payload = {
        event: event,
        funnel_attribution: funnelAttribution,
        ecommerce: ecommercePayload,
        ...getDataLayerUserPayload(),
    };

    window.dataLayer!.push({ ecommerce: null });
    window.dataLayer!.push(payload);
};

const sendDataLayerFormEvent = async (
    event: string,
    formName: string,
    formId: string,
) => {
    await checkIfDataLayerReady();
    await checkIfUserAuthReady();

    const payload = {
        event: event,
        form_name: formName,
        form_id: formId,
        ...getDataLayerUserPayload(),
    };

    window.dataLayer!.push(payload);
};

const sendDataLayerQuizEvent = async (
    event: string,
    quizStep: string,
    quizQuestion: string,
    quizAnswer: string,
    quizQuestionValue: string,
    quizAnswerValue: string,
    quizResultMain: string,
    quizResultAlternate: string,
    quizResultAlternate2: string,
) => {
    if (event == 'quiz_started') {
        await checkIfDataLayerReady();
        await checkIfUserAuthReady();

        const payload = {
            event: event,
            event_group: 'Quiz',
            ...getDataLayerUserPayload(),
        };

        window.dataLayer!.push(payload);
        return;
    } else if (event == 'quiz_complete') {
        await checkIfDataLayerReady();
        const payload = {
            event: event,
            quiz_result_main: quizResultMain,
            quiz_result_alternate: quizResultAlternate,
            quiz_result_alternate2: quizResultAlternate2,
            event_group: 'Quiz',
            ...getDataLayerUserPayload(),
        };

        window.dataLayer!.push(payload);
        return;
    }

    await checkIfDataLayerReady();
    await checkIfUserAuthReady();

    const payload = {
        event: event,
        quiz_step: quizStep,
        quiz_question: quizQuestion,
        quiz_answer: quizAnswer,
        quiz_question_value: quizQuestionValue,
        quiz_answer_value: quizAnswerValue,
        event_group: 'Quiz',
        ...getDataLayerUserPayload(),
    };

    window.dataLayer!.push(payload);
};

const sendDataLayerDesignLabEvent = async (
    event: string,
    productType: string,
    theme: string,
    size: string,
    material: string,
) => {
    await checkIfDataLayerReady();
    await checkIfUserAuthReady();

    const payload = {
        event: event,
        product_type: productType,
        theme: theme,
        size: size,
        material: material,
        event_group: 'Design Lab',
        ...getDataLayerUserPayload(),
    };

    window.dataLayer!.push(payload);
};

const sendDataLayerCustomProjectEvent = async (
    event: string,
    projectId: string,
    selectedDesigner: string,
    productType: string,
    size: string,
    material: string,
    additionalOptions: string,
) => {
    await checkIfDataLayerReady();
    await checkIfUserAuthReady();

    const payload = {
        event: event,
        project_id: projectId,
        selected_designer: selectedDesigner,
        product_type: productType,
        size: size,
        material: material,
        additional_options: additionalOptions,
        event_group: 'Custom Project',
        ...getDataLayerUserPayload(),
    };

    window.dataLayer!.push(payload);
};

const sendDataLayerQuoteEvent = async (
    event: string,
    quoteId: number | null,
    quotedPrice: number | null,
    productName: unknown,
    productQuantity: unknown,
    productContents: unknown,
    productSize: unknown,
) => {
    await checkIfDataLayerReady();
    await checkIfUserAuthReady();

    const payload = {
        event: event,
        quote_id: quoteId,
        quoted_price: quotedPrice,
        product_name: normalizeDataLayerString(productName),
        product_quantity: productQuantity,
        product_contents: normalizeDataLayerString(productContents),
        product_size: normalizeDataLayerString(productSize),
        event_group: 'Quote',
        ...getDataLayerUserPayload(),
    };

    window.dataLayer!.push(payload);
};

const sendDataLayerDesignerEvent = async (
    event: string,
    designerName: string,
) => {
    await checkIfDataLayerReady();
    await checkIfUserAuthReady();

    const payload = {
        event: event,
        designer_name: designerName,
        ...getDataLayerUserPayload(),
    };

    window.dataLayer!.push(payload);
};

const sendDataLayerFilterEvent = async (
    event: string,
    filterFor: string,
    filterCategory: string,
    filterSelection: string,
) => {
    await checkIfDataLayerReady();
    await checkIfUserAuthReady();

    const payload = {
        event: event,
        filter_for: filterFor,
        filter_category: filterCategory,
        filter_selection: filterSelection,
        event_group: 'Filter',
        ...getDataLayerUserPayload(),
    };

    window.dataLayer!.push(payload);
};

const sendDataLayerFaqEvent = async (event: string, faqQuestion: string) => {
    await checkIfDataLayerReady();
    await checkIfUserAuthReady();

    const payload = {
        event: event,
        faq_question: faqQuestion,
        ...getDataLayerUserPayload(),
    };

    window.dataLayer!.push(payload);
};

const getSelectedThemeNameForCanvas = (canvas: any) => {
    if (canvas) {
        for (let i = 0; i < canvas?.objects?.length; i++) {
            if (canvas.objects[i].name == 'theme') {
                return canvas.objects[i].themeName ?? 'No Theme Selected';
            }
        }
    }
    return 'No Theme Selected';
};

const normalizeDataLayerString = (value: unknown): string | undefined => {
    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number') {
        return String(value);
    }

    return undefined;
};

const normalizeEcommerceItemAttribute = (attribute: any) => {
    if (attribute && typeof attribute == 'string') {
        return attribute
            .toString()
            .replace(/-/g, ' ')
            .replace(/_/g, ' ')
            .replace(/(?:^|[\s"'([{])+\S/g, (match: any) => match.toUpperCase())
            .replace(/ X /g, ' x ')
            .replace(/ {2,}/g, ' ');
    }

    return attribute;
};

const getDataLayerUserPayload = () => {
    const { user } = useAuth();

    return {
        user_id: user?.value?.id,
        name: user?.value?.name,
        email: user?.value?.email,
        company_id: user?.value?.company?.id,
        company: user?.value?.company?.name,
        company_purchase_count: user?.value?.company?.ordersCount,
        company_purchase_value: user?.value?.company?.ordersValue,
        impersonating: !!user?.value?.impersonateUserName,
    };
};

const checkIfDataLayerReady = () => {
    if (!import.meta.client) {
        if (import.meta.dev) {
            throw new Error(
                'sendDataLayerPageView must be called client-side only',
            );
        }
    }

    return new Promise((resolve) => {
        if (typeof window === 'undefined') {
            resolve(true);
            return;
        }

        if (window?.dataLayer) {
            resolve(true);
            return;
        }

        const interval = setInterval(() => {
            if (window?.dataLayer) {
                clearInterval(interval);
                resolve(true);
            }
        }, 100);
    });
};

const checkIfUserAuthReady = () => {
    return new Promise((resolve) => {
        if (typeof window === 'undefined') {
            resolve(true);
            return;
        }

        const start = Date.now();
        const interval = setInterval(() => {
            const { loading } = useAuth();

            if (loading.value === false || Date.now() - start > 15000) {
                clearInterval(interval);
                resolve(true);
            }
        }, 100);
    });
};

const sendDataLayerChatWidgetOpenedEvent = async (source: string) => {
    await checkIfDataLayerReady();

    const payload = {
        event: 'chat_widget_opened',
        chat_source: source,
    };

    window.dataLayer!.push(payload);
};

export {
    getSelectedThemeNameForCanvas,
    sendDataLayerChatWidgetOpenedEvent,
    sendDataLayerCustomProjectEvent,
    sendDataLayerDesignLabEvent,
    sendDataLayerDesignerEvent,
    sendDataLayerEcommerceEvent,
    sendDataLayerEvent,
    sendDataLayerFaqEvent,
    sendDataLayerFilterEvent,
    sendDataLayerFormEvent,
    sendDataLayerPageView,
    sendDataLayerQuizEvent,
    sendDataLayerQuoteEvent,
    sendDataLayerSearchEvent,
};
